(() => {
  if (window.nrouteEssentialCartEnhancementInitialized) return;
  window.nrouteEssentialCartEnhancementInitialized = true;

  const portalSelector = "#essential-cart-drawer-portal";
  const milestoneSelector = `${portalSelector} [data-essential-cart-element="reward-milestone"]`;
  const popupClass = "nroute-milestone-popup";
  const popupSettings = window.nrouteRewardPopupSettings || {};
  const progressFillSelector = `${portalSelector} [data-essential-cart-element="reward-progress-bar-fill"]`;
  let previousMilestoneStates = null;
  let milestoneQueue = [];
  let activeMilestoneIndex = null;
  let popupTimer = null;
  let progressFillListenerAttached = false;

  const getMilestoneState = (milestone, index) => {
    const ariaLabel = milestone.getAttribute("aria-label") || "";
    const match = ariaLabel.match(/\b(unlocked|locked)\b/i);
    const visibleLabel =
      milestone
        .querySelector('[data-essential-cart-element="reward-milestone-label"]')
        ?.textContent?.trim() || "";
    const accessibleLabel = match
      ? ariaLabel.replace(match[0], "").replace(/^[\s:,-]+|[\s:,-]+$/g, "").trim()
      : "";
    const label = visibleLabel || accessibleLabel;
    const key = label.replace(/\s+/g, " ").toLocaleLowerCase();
    const popupIndex = index;

    return {
      index,
      label,
      status: match ? match[1].toLowerCase() : "",
      key,
      popupIndex,
    };
  };

  const getConfiguredMilestoneValue = (values, index) => {
    if (!Array.isArray(values)) return "";
    const value = values[index];
    return typeof value === "string" ? value.trim() : "";
  };

  const getMilestonePosition = (milestone) => {
    const position = milestone.style.left || window.getComputedStyle(milestone).left;
    const value = Number.parseFloat(position);
    return Number.isFinite(value) ? value : null;
  };

  const getPopupMilestoneValue = (values, index) => {
    const value = getConfiguredMilestoneValue(values, index);
    if (value) return value;

    return index === 2 ? getConfiguredMilestoneValue(values, 3) : "";
  };

  const isMilestoneVisible = (milestone) => {
    if (!milestone.isConnected) return false;

    const visibleElement = milestone.querySelector(
      '[data-essential-cart-element="reward-milestone-icon"]',
    );
    if (!visibleElement) return false;

    let rect = visibleElement.getBoundingClientRect();
    let left = Math.max(0, rect.left);
    let top = Math.max(0, rect.top);
    let right = Math.min(window.innerWidth, rect.right);
    let bottom = Math.min(window.innerHeight, rect.bottom);

    if (right <= left || bottom <= top) return false;

    for (let element = visibleElement; element; element = element.parentElement) {
      const style = window.getComputedStyle(element);
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        Number(style.opacity) === 0
      ) {
        return false;
      }

      const elementRect = element.getBoundingClientRect();
      const clipsX = /hidden|clip|auto|scroll/.test(style.overflowX);
      const clipsY = /hidden|clip|auto|scroll/.test(style.overflowY);
      if (clipsX || clipsY) {
        const clipLeft = elementRect.left + element.clientLeft;
        const clipTop = elementRect.top + element.clientTop;
        if (clipsX) {
          left = Math.max(left, clipLeft);
          right = Math.min(right, clipLeft + element.clientWidth);
        }
        if (clipsY) {
          top = Math.max(top, clipTop);
          bottom = Math.min(bottom, clipTop + element.clientHeight);
        }
        if (right <= left || bottom <= top) return false;
      }
    }

    return true;
  };

  const readMilestoneStates = () => {
    const allMilestones = Array.from(document.querySelectorAll(milestoneSelector));
    const positions = Array.from(
      new Set(
        allMilestones
          .map(getMilestonePosition)
          .filter((position) => position !== null)
          .map((position) => Math.round(position * 1000) / 1000),
      ),
    ).sort((left, right) => left - right);

    return allMilestones
      .filter((milestone) =>
        milestone.querySelector('[data-essential-cart-element="reward-milestone-icon"]'),
      )
      .map((milestone) => {
        const position = getMilestonePosition(milestone);
        const roundedPosition =
          position === null ? null : Math.round(position * 1000) / 1000;
        const index = positions.indexOf(roundedPosition);

        return {
          ...getMilestoneState(milestone, index),
          element: milestone,
        };
      })
      .filter((milestone) => milestone.index >= 0);
  };

  const updateMilestoneText = (milestones) => {
    milestones.forEach((state) => {
      const milestone = state.element;
      const customText = getConfiguredMilestoneValue(popupSettings.milestoneTexts, state.popupIndex);
      const textElements = milestone.querySelectorAll(":scope > .nroute-milestone-custom-text");
      const textElement = textElements[0];

      textElements.forEach((duplicate, duplicateIndex) => {
        if (duplicateIndex > 0) duplicate.remove();
      });

      if (!customText) {
        textElement?.remove();
        return;
      }

      if (!textElement) {
        const newTextElement = document.createElement("span");
        newTextElement.className = "nroute-milestone-custom-text";
        newTextElement.setAttribute("data-nroute-milestone-custom-text", "");
        milestone.prepend(newTextElement);
        newTextElement.textContent = customText;
        return;
      }

      if (textElement.textContent !== customText) textElement.textContent = customText;
    });
  };

  const formatRewardMessage = (rewardLabel) => {
    const rewardText = typeof rewardLabel === "string" && rewardLabel.trim() ? rewardLabel.trim() : "your reward";
    const template = popupSettings.messageTemplate || "You unlocked {{ reward_label }}!";
    return template.replace(/\{\{\s*reward_label\s*\}\}/gi, rewardText);
  };

  const closePopup = (popup) => {
    popup.remove();
    activeMilestoneIndex = null;
    showNextPopup();
  };

  const showNextPopup = () => {
    if (document.body.querySelector(`.${popupClass}`)) return;

    // Only celebrate the highest unlocked milestone; drop lower queued popups.
    const states = readMilestoneStates();
    let milestone = null;
    milestoneQueue.forEach((queuedMilestone) => {
      const currentMilestone = states.find((state) => state.index === queuedMilestone.index);
      if (
        currentMilestone?.status === "unlocked" &&
        isMilestoneVisible(currentMilestone.element) &&
        (!milestone || queuedMilestone.index > milestone.index)
      ) {
        milestone = queuedMilestone;
      }
    });
    milestoneQueue = [];
    if (!milestone) return;

    const popup = document.createElement("div");
    popup.className = popupClass;
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "true");
    popup.setAttribute("aria-labelledby", "nroute-milestone-title");
    popup.setAttribute("aria-describedby", "nroute-milestone-message");

    const card = document.createElement("div");
    card.className = "nroute-milestone-popup__card";

    const confetti = document.createElement("div");
    confetti.className = "nroute-milestone-popup__confetti";
    confetti.setAttribute("aria-hidden", "true");
    for (let index = 0; index < 28; index += 1) {
      const piece = document.createElement("span");
      piece.style.setProperty("--confetti-index", `${index}`);
      piece.style.setProperty("--confetti-delay", `-${(index % 9) * 400}ms`);
      confetti.append(piece);
    }

    const closeButton = document.createElement("button");
    closeButton.className = "nroute-milestone-popup__close";
    closeButton.type = "button";
    closeButton.setAttribute("aria-label", popupSettings.closeAriaLabel || "Close reward popup");
    closeButton.textContent = "×";

    const icon = document.createElement("div");
    icon.className = "nroute-milestone-popup__icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "🎉";

    const title = document.createElement("h2");
    title.className = "nroute-milestone-popup__title";
    title.id = "nroute-milestone-title";
    const customTitle = getPopupMilestoneValue(popupSettings.milestonePopupTitles, milestone.popupIndex);
    title.textContent = customTitle || popupSettings.title || "You unlocked a reward!";

    const message = document.createElement("p");
    message.className = "nroute-milestone-popup__message";
    message.id = "nroute-milestone-message";
    const customMessage = getPopupMilestoneValue(popupSettings.milestonePopupTexts, milestone.popupIndex);
    message.textContent = customMessage || formatRewardMessage(milestone.label);

    const dismissButton = document.createElement("button");
    dismissButton.className = "nroute-milestone-popup__dismiss";
    dismissButton.type = "button";
    dismissButton.textContent = popupSettings.buttonLabel || "Awesome!";

    closeButton.addEventListener("click", () => closePopup(popup));
    dismissButton.addEventListener("click", () => closePopup(popup));
    popup.addEventListener("click", (event) => {
      if (event.target === popup) closePopup(popup);
    });
    popup.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePopup(popup);
    });

    card.append(closeButton, icon, title, message, dismissButton);
    popup.append(confetti, card);
    document.body.append(popup);
    activeMilestoneIndex = milestone.index;
    dismissButton.focus();
  };

  const scanMilestones = () => {
    document
      .querySelectorAll(`${portalSelector} [role="listitem"]`)
      .forEach((item) => {
        const properties = item.querySelector(
          '[data-essential-cart-element="line-item-properties"]',
        );
        const propertyLines = Array.from(properties?.children || []).map((property) =>
          property.textContent.trim(),
        );
        const isAssortedPack =
          propertyLines.some((property) => property.startsWith("Pack:")) &&
          propertyLines.some((property) => property.startsWith("Flavours:"));
        const variantSelector = item.querySelector(
          '[aria-label^="Select variant for"]',
        );
        const selectorWrapper = variantSelector?.parentElement;

        if (selectorWrapper) {
          selectorWrapper.classList.toggle(
            "nroute-assorted-pack-variant-selector",
            isAssortedPack,
          );
        }
      });

    const current = readMilestoneStates();
    updateMilestoneText(current);
    if (current.length === 0) return;
    const currentStates = new Map(
      current.map((milestone) => [
        milestone.index,
        {
          status: milestone.status,
          hasLabel: Boolean(milestone.label),
          notified: milestone.status === "unlocked",
        },
      ]),
    );

    if (previousMilestoneStates === null) {
      previousMilestoneStates = currentStates;
      return;
    }

    current.forEach((milestone) => {
      const previousState = previousMilestoneStates.get(milestone.index);
      const isNewlyUnlocked =
        milestone.status === "unlocked" &&
        (previousState?.status === "locked" ||
          (previousState?.status === "unlocked" && !previousState.hasLabel && Boolean(milestone.label)));

      if (isNewlyUnlocked && !previousState?.notified) {
        if (
          isMilestoneVisible(milestone.element) &&
          !milestoneQueue.some((queued) => queued.index === milestone.index) &&
          activeMilestoneIndex !== milestone.index
        ) {
          milestoneQueue.push({
            index: milestone.index,
            popupIndex: milestone.popupIndex,
            label: milestone.label,
          });
        }
        currentStates.set(milestone.index, {
          status: milestone.status,
          hasLabel: Boolean(milestone.label),
          notified: true,
        });
      } else if (milestone.status === "unlocked" && previousState?.notified) {
        currentStates.set(milestone.index, {
          status: milestone.status,
          hasLabel: Boolean(milestone.label),
          notified: true,
        });
      }
    });
    previousMilestoneStates = currentStates;
    schedulePopup();
  };

  // Show the popup once the progress bar finishes animating, so a cart update
  // that crosses several milestones only celebrates the final (highest) one.
  const schedulePopup = () => {
    if (popupTimer) {
      clearTimeout(popupTimer);
      popupTimer = null;
    }

    const fill = document.querySelector(progressFillSelector);
    const duration = fill
      ? Number.parseFloat(window.getComputedStyle(fill).transitionDuration) || 0
      : 0;

    if (fill && duration > 0) {
      if (!progressFillListenerAttached) {
        progressFillListenerAttached = true;
        fill.addEventListener("transitionend", (event) => {
          if (event.propertyName !== "width") return;
          if (popupTimer) {
            clearTimeout(popupTimer);
            popupTimer = null;
          }
          showNextPopup();
        });
      }
      // Fallback in case transitionend does not fire (e.g. reduced motion).
      popupTimer = setTimeout(() => {
        popupTimer = null;
        showNextPopup();
      }, duration * 1000 + 150);
      return;
    }

    // No animating bar: show on the next frame.
    popupTimer = setTimeout(() => {
      popupTimer = null;
      showNextPopup();
    }, 50);
  };

  const openDrawerAfterAdd = () => {
    const cartApi = window.essentialCartApi;
    if (!cartApi || typeof cartApi.openCart !== "function") {
      console.error("[NRoute] Essential Cart API is unavailable after item-added.");
      return;
    }

    if (typeof cartApi.isOpen !== "function" || !cartApi.isOpen()) {
      cartApi.openCart();
    }
  };

  scanMilestones();
  new MutationObserver(scanMilestones).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["aria-label"],
    childList: true,
    subtree: true,
  });

  document.addEventListener("essential-cart:item-added", openDrawerAfterAdd);
})();
